import { AlertCircle, Mail, Paperclip } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorState, Spinner } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import { useAuth } from "@/lib/auth";
import {
  emailLine, MESSAGE_STATUS_LABELS, messageStatus, type MessageActivity, type MessageStatus,
} from "@/lib/conversations";
import { emailFrameDoc, emailLinks } from "@/lib/email-view";
import { useMessage } from "@/lib/queries";
import { automatedBy, seesAutomation } from "@/lib/texts";
import { atLeast, type MessageDetail } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * An email in a thread or on the record (ADR-180 in platform): one line that says what it is, who
 * it was to or from and when, and opens the whole email. A list never carries more than an email's
 * first words, so a long email, or a thread of them, never fills the screen, and its HTML is never
 * drawn anywhere but the dialog's sandboxed frame.
 */

/** "23 Oct": the day an email line names, on this browser's clock. */
export function emailDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * The line. `name` is the contact's, for the words; `address` theirs as this viewer sees it, so a
 * reply from somewhere else says so. `live`: the email is core-api's, and the dialog reads it whole
 * from `GET /messages/{id}`; on the development sample it draws what the activity carries.
 */
export function EmailLine({
  message: m, name, address, live, className,
}: {
  message: MessageActivity;
  name: string;
  address?: string | null;
  live: boolean;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const sees = seesAutomation(useAuth().user?.role);
  const line = emailLine(m, name, emailDay, address);
  const out = m.direction === "outbound";
  const failed = m.status === "failed";
  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className={cn(
          "block w-full min-w-0 rounded-xl border bg-surface px-3 py-2 text-left transition-colors hover:bg-surface-hover",
          failed ? "border-danger-6" : out ? "border-accent-6" : "border-border",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5 text-sm text-text">
          <Mail className="size-3.5 shrink-0 text-text-muted" aria-hidden />
          <span className="min-w-0 truncate">{line.text}</span>
        </span>
        {line.note && <span className="mt-0.5 block text-2xs text-text-subtle">{line.note}</span>}
        {m.body && <span className="mt-0.5 block truncate text-xs text-text-muted">{m.body}</span>}
        {out && (
          <span className={cn("mt-0.5 flex items-center gap-1 text-2xs text-text-subtle", failed && "text-danger-11")}>
            {failed && <AlertCircle className="size-3" aria-hidden />}
            {/* A workflow's email names the workflow only to a supervisor and up (ACL-591). */}
            {m.author?.name ?? automatedBy(m.workflow, sees)} · {MESSAGE_STATUS_LABELS[m.status]}
            {failed && m.error ? `: ${m.error}` : ""}
          </span>
        )}
      </button>
      <EmailDialog
        messageId={m.messageId}
        activity={m}
        live={live}
        open={open}
        onOpenChange={setOpen}
        summary={line.text}
      />
    </>
  );
}

/** An email as the dialog shows it, from core-api's whole message or from the sample's activity. */
interface EmailView {
  subject: string | null;
  /** The real sender: their name as they gave it and the address it came from. */
  from: string;
  to: string[];
  cc: string[];
  at: string;
  status: MessageStatus;
  error: string | null;
  text: string;
  html: string | null;
  attachments: number;
  replyAddress: string | null;
  messageId: string | null;
}

/** Said where the lead form hides the contact's address from this viewer. */
const HIDDEN = "Not shown";

function fromDetail(d: MessageDetail): EmailView {
  const inbound = d.direction === "inbound";
  const address = d.from_number.trim();
  const from = d.from_name?.trim()
    ? address ? `${d.from_name.trim()} <${address}>` : d.from_name.trim()
    : address || HIDDEN;
  const to = d.email_to.length > 0 ? d.email_to : d.to_number ? [d.to_number] : [];
  return {
    subject: d.subject ?? null,
    from,
    to,
    cc: d.email_cc,
    at: d.at,
    status: messageStatus(d.status, inbound),
    error: inbound ? null : d.error_detail ?? d.error,
    text: d.body,
    html: d.html,
    attachments: d.media_count,
    replyAddress: d.reply_address,
    messageId: d.email_message_id,
  };
}

function fromActivity(a: MessageActivity): EmailView {
  return {
    subject: a.subject ?? null,
    from: a.direction === "outbound" ? a.author?.name ?? "Us" : a.from ?? HIDDEN,
    to: [],
    cc: [],
    at: a.at,
    status: a.status,
    error: a.error ?? null,
    text: a.body,
    html: null,
    attachments: 0,
    replyAddress: null,
    messageId: null,
  };
}

/**
 * The whole email: who it was from and to, when, where it stands, and its text. Its HTML, when it
 * came with one, is offered behind "Show formatting" and drawn only in a sandboxed frame
 * (`lib/email-view.ts`): no script, nothing fetched, a link that goes nowhere. The links are listed
 * under it as text. A supervisor and above also see the thread's reply address and the
 * Message-ID, for support and for testing the replies by hand.
 *
 * `live` asks core-api for it while the dialog is open; the development sample asks nothing.
 */
export function EmailDialog({
  messageId, activity, live, open, onOpenChange, summary,
}: {
  messageId: string;
  activity: MessageActivity;
  live: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The line the dialog was opened from, as its description. */
  summary?: string;
}) {
  const read = useMessage(open && live ? messageId : undefined);
  const view = live ? (read.data ? fromDetail(read.data) : null) : fromActivity(activity);
  const subject = view?.subject ?? activity.subject ?? null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className={cn(!subject && "text-text-muted")}>{subject || "No subject"}</DialogTitle>
          <DialogDescription>{summary ?? "An email"}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {view ? (
            <EmailBody view={view} />
          ) : read.isError ? (
            <ErrorState error={read.error} title="Could not open the email" className="py-8" />
          ) : (
            <div className="flex justify-center py-8" role="status">
              <Spinner className="size-5" />
              <span className="sr-only">Opening the email</span>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function EmailBody({ view }: { view: EmailView }) {
  const { user } = useAuth();
  const [formatted, setFormatted] = React.useState(false);
  const support = atLeast(user?.role, "supervisor");
  const failed = view.status === "failed";
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-text-muted">From</dt>
        <dd className="min-w-0 break-words text-text">{view.from}</dd>
        {view.to.length > 0 && (
          <>
            <dt className="text-text-muted">To</dt>
            <dd className="min-w-0 break-words text-text">{view.to.map((t) => t || HIDDEN).join(", ")}</dd>
          </>
        )}
        {view.cc.length > 0 && (
          <>
            <dt className="text-text-muted">Cc</dt>
            <dd className="min-w-0 break-words text-text">{view.cc.join(", ")}</dd>
          </>
        )}
        <dt className="text-text-muted">Date</dt>
        <dd className="text-text">{formatDateTime(view.at)}</dd>
        <dt className="text-text-muted">Status</dt>
        <dd className={cn("text-text", failed && "text-danger-11")}>
          {MESSAGE_STATUS_LABELS[view.status]}
          {failed && view.error ? `: ${view.error}` : ""}
        </dd>
      </dl>

      {view.html && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" aria-pressed={formatted} onClick={() => setFormatted((on) => !on)}>
            {formatted ? "Show as text" : "Show formatting"}
          </Button>
        </div>
      )}
      {formatted && view.html ? (
        <FormattedEmail html={view.html} />
      ) : (
        <p className="whitespace-pre-wrap break-words rounded-lg border border-border bg-surface-sunken px-3 py-2 text-sm text-text">
          {view.text || "This email has no text."}
        </p>
      )}

      {view.attachments > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-text-muted">
          <Paperclip className="size-3.5" aria-hidden />
          {view.attachments === 1 ? "1 attachment was not kept" : `${view.attachments} attachments were not kept`}
        </p>
      )}

      {support && (view.replyAddress || view.messageId) && (
        <div className="space-y-0.5 border-t border-border pt-3 text-xs text-text-muted">
          {view.replyAddress && (
            <p>
              Replies come back to <span className="select-all break-all font-mono text-text">{view.replyAddress}</span>
            </p>
          )}
          {view.messageId && (
            <p>
              Message-ID <span className="select-all break-all font-mono text-text">{view.messageId}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** The email as it was formatted, in a frame that can do nothing, and its links as text. */
function FormattedEmail({ html }: { html: string }) {
  const doc = React.useMemo(() => emailFrameDoc(html), [html]);
  const links = React.useMemo(() => emailLinks(html), [html]);
  return (
    <div className="space-y-3">
      <iframe
        sandbox=""
        srcDoc={doc}
        referrerPolicy="no-referrer"
        title="The email as it was formatted"
        className="h-[28rem] w-full rounded-lg border border-border bg-white"
      />
      {links.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-text-muted">Links in this email. They open nothing here: copy one to open it.</p>
          <ul className="space-y-0.5">
            {links.map((url) => (
              <li key={url} className="flex items-start gap-1">
                <span className="min-w-0 flex-1 select-all break-all font-mono text-xs text-text">{url}</span>
                <CopyButton value={url} label="Copy link" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
